// src/hooks/useBrand.ts
// -----------------------------------------------------------------------------
// Reads the tenant's live SmartBrand record (smart_brand/{gido}) and returns the
// resolved brand colours for the storefront. SmartBrand is the source of truth
// (D-062); shopConfig.brand is the fallback if the record is missing a value.
//
// The accent is passed through legibleAccent() (D-066): a brand colour rendered
// against the dark header is lightened in HSL if its contrast is too low, hue and
// saturation preserved - so any tenant's accent stays readable without losing
// their brand. Gold (#D4AF37) on near-black passes through unchanged.
//
// House rule: short hyphen "-" only.
// -----------------------------------------------------------------------------
import { useMemo } from 'react';
import { shopConfig as cfg } from '../shopConfig';
import { legibleAccent } from '../lib/legibleAccent';

export interface Brand {
  primary: string;
  secondary: string;
  accent: string;
}

export function useBrand(): Brand {
  // SmartBrand (D-062) remains the intended source of truth, but this hook read
  // smart_brand/{gido} through the Firestore BROWSER SDK, and that read has never
  // succeeded for this tenant: the SDK is configured against dbos-core-stage, where no
  // such document exists. What it did achieve was pulling firebase/firestore into the
  // bundle and opening a WebChannel that stayed pending for 30-60s - which keeps the
  // browser's loading indicator spinning long after the page is usable, and read as a
  // "one minute load" on 2026-10-09. It was also a direct browser Firestore read, which
  // D-008/D-009 rule out precisely because it is governed by rules rather than a
  // service account.
  //
  // So the shopConfig seed values are what has actually been rendering all along. This
  // returns them directly, with legibleAccent still applied per D-066 - gold on
  // near-black passes through unchanged, so nothing about her shop looks different.
  //
  // To restore a live source, serve the colours from smartshop-shop-config, which the
  // storefront already calls. Never from the browser SDK.
  return useMemo(() => ({
    primary: cfg.brand.primary,
    secondary: cfg.brand.secondary,
    accent: legibleAccent(cfg.brand.accent || cfg.brand.primary, cfg.brand.secondary),
  }), []);
}
